import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { MileageRatesDocument } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import Footer from 'modules/core/components/Sidepanel/Footer'

import FilesPreviewer from 'modules/expenses/components/expenses/Detail/_FilesPreviewer'
import MileageForm from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new'
import { getMileageFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { MileageSchema } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/schema'
import ReuseMileageList from 'modules/expenses/components/expenses/Detail/_newForm/CreateMileageForm/ReuseMileageList'
import type { ReusableMileage } from 'modules/expenses/components/expenses/Detail/_newForm/CreateMileageForm/ReuseMileageList'
import Sidepanel from 'modules/expenses/components/expenses/Detail/_Sidepanel'
import { BulkActionsProvider } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { FilesPreviewerProvider } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import CreateExpenseFooterActions from 'modules/expenses/components/expenses/New/NewForm/CreateExpenseFooterActions'
import { useEmployeeAccess } from 'modules/expenses/hooks/useEmployeeAccess'
import { normalizeEffectiveOn } from 'modules/expenses/hooks/useExpensable'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

export interface CreateMileageFormProps {
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  canSeeEmployeeLegalEntity: boolean
  canCreateExpensesForOthers: boolean
  customizationFields: CustomizationField[]
  hasProjectsFeature: boolean
  groupCurrency?: string | null
  hasExpensablesF0Forms?: boolean
  hasMileageReuseTrip?: boolean
}

const CreateMileageForm = (props: CreateMileageFormProps) => {
  const {
    onClose,
    customizationFields,
    hasProjectsFeature,
    canSeeEmployeeLegalEntity,
    canCreateExpensesForOthers,
    hasReportOnBehalfFeature,
    groupCurrency,
  } = props
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [_openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }
  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }
  const { employee, access, company } = useCurrentSession()
  const { groupId } = useParams()

  const [formEmployeeId, setFormEmployeeId] = useState<number>()
  const [showReuseMileageList, setShowReuseMileageList] = useState(false)
  const [hasReusedTrip, setHasReusedTrip] = useState(false)
  const [selectedReuseMileage, setSelectedReuseMileage] = useState<ReusableMileage>()

  const employeeId = formEmployeeId ?? employee?.id

  const defaultValues = getMileageFormDefaultValues({
    employee,
    customizationFields,
    groupId: groupId ? Number(groupId) : null,
    groupCurrency,
  })

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }
  const [formEffectiveOn, setFormEffectiveOn] = useState(defaultValues.effectiveOn)

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
  })

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const mileageForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      MileageSchema({
        customizationFields,
        resourceType: 'mileage',
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
      })
    ),
    defaultValues: {
      ...defaultValues,
    },
  })

  const { watch } = mileageForm
  const { accessId: newFormAccessId } = useEmployeeAccess(formEmployeeId)

  const currency = watch('currency')

  // f0 fields use setValue() which doesn't trigger parent re-renders.
  // We need to watch internalReference so that the formValues snapshot
  // passed to CreateExpenseFooterActions stays fresh on submit.
  if (props.hasExpensablesF0Forms) {
    watch('internalReference')
  }

  const { data } = useQuery(MileageRatesDocument, {
    variables: {
      companyId: company.id,
      accessId: newFormAccessId || access.id,
      currency: currency,
    },
  })

  const ratesOptions = data?.expenses?.rateConfigurationsConnection?.nodes ?? []
  const { trigger } = mileageForm
  useUnsafeEffect(() => {
    trigger()
  }, [customizationFields, trigger])

  const handleReuseTrip = () => {
    setShowReuseMileageList(true)
  }

  const handleReuseMileageSelectionChange = (mileage: ReusableMileage | undefined) => {
    setSelectedReuseMileage(mileage)
  }

  const handleConfirmReuseMileage = () => {
    if (!selectedReuseMileage) return

    const mileage = selectedReuseMileage
    // Copy trip-related fields from the selected mileage into the current form.
    // We preserve the current effectiveOn, group, employee, and files.
    // We do NOT copy mileage/calculatedMileage — the distance calculation hook
    // will recalculate automatically when origin/destination/stops are set.
    // We also do NOT copy amount — it is auto-calculated from distance * rate.
    const m = mileage.mileage

    mileageForm.setValue('from', m?.from ?? '', { shouldDirty: true, shouldValidate: true })
    mileageForm.setValue('to', m?.to ?? '', { shouldDirty: true, shouldValidate: true })
    mileageForm.setValue('originLatitude', m?.originLatitude ?? '', { shouldDirty: true })
    mileageForm.setValue('originLongitude', m?.originLongitude ?? '', { shouldDirty: true })
    mileageForm.setValue('destinationLatitude', m?.destinationLatitude ?? '', { shouldDirty: true })
    mileageForm.setValue('destinationLongitude', m?.destinationLongitude ?? '', {
      shouldDirty: true,
    })
    mileageForm.setValue('roundTrip', m?.roundTrip ?? false, { shouldDirty: true })
    mileageForm.setValue(
      'stops',
      (m?.mileageStops ?? []).map((s) => ({
        name: s.name ?? '',
        latitude: s.latitude ?? '',
        longitude: s.longitude ?? '',
      })),
      { shouldDirty: true, shouldValidate: true }
    )
    mileageForm.setValue('units', m?.units ?? 'km', { shouldDirty: true })
    mileageForm.setValue('description', mileage.description ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    mileageForm.setValue('internalReference', mileage.internalReference ?? '', {
      shouldDirty: true,
    })
    mileageForm.setValue('currency', mileage.currency, { shouldDirty: true })

    if (m?.rate) {
      mileageForm.setValue('rate', parseFloat(m.rate), {
        shouldDirty: true,
        shouldValidate: true,
      })
    }

    if (m?.category?.id) {
      mileageForm.setValue('category', m.category.id, { shouldDirty: true })
    }

    mileageForm.setValue('subcategory', m?.subcategory ?? '', { shouldDirty: true })

    setShowReuseMileageList(false)
    setHasReusedTrip(true)
    setSelectedReuseMileage(undefined)
  }

  const handleReuseMileageGoBack = () => {
    setShowReuseMileageList(false)
    setSelectedReuseMileage(undefined)
  }

  const showReuseButton = props.hasMileageReuseTrip && !hasReusedTrip

  return (
    <BulkActionsProvider>
      <Sidepanel
        open={openSidepanel}
        onClose={handleClose}
        onCloseClicked={handleCloseClicked}
        canRestartApprovalFlow={false}
      >
        <FilesPreviewerProvider initialFiles={[]}>
          <FilesPreviewer
            onClose={handleClose}
            onDrop={onDropHandler}
            showUploadButton
            isFilesFieldEnabled={customizationFields.some(
              (field) => field.id === 'mileage-files' && field.enabled
            )}
          />

          <FormProvider {...mileageForm}>
            {showReuseMileageList ? (
              <Box flexDirection="column" style={{ minHeight: '100%' }}>
                <Box flexGrow>
                  <ReuseMileageList
                    selectedMileageId={selectedReuseMileage?.id}
                    onSelectionChange={handleReuseMileageSelectionChange}
                  />
                </Box>
                <Box position="sticky" style={{ bottom: '0' }}>
                  <Footer
                    content={
                      <Box
                        width="full"
                        flexDirection="row"
                        justifyContent="flexEnd"
                        alignItems="center"
                        paddingX="s8"
                        gap="s8"
                      >
                        {props.hasExpensablesF0Forms ? (
                          <>
                            <F0Button
                              variant="outline"
                              label={i18n.t('expenses_v2.forms.new_form.reuse_trip.go_back')}
                              onClick={handleReuseMileageGoBack}
                            />
                            <F0Button
                              variant="default"
                              label={i18n.t('expenses_v2.forms.new_form.reuse_trip.select_mileage')}
                              onClick={handleConfirmReuseMileage}
                              disabled={!selectedReuseMileage}
                            />
                          </>
                        ) : (
                          <>
                            <Button type="secondary" onClick={handleReuseMileageGoBack}>
                              {i18n.t('expenses_v2.forms.new_form.reuse_trip.go_back')}
                            </Button>
                            <Button
                              type="primary"
                              onClick={handleConfirmReuseMileage}
                              disabled={!selectedReuseMileage}
                            >
                              {i18n.t('expenses_v2.forms.new_form.reuse_trip.select_mileage')}
                            </Button>
                          </>
                        )}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            ) : (
              <>
                <Box padding="s24">
                  <MileageForm
                    customizationFields={customizationFields}
                    budgetOptions={newBudgetOptions}
                    projectWorkers={newProjectWorkers}
                    hasProjectsFeature={hasProjectsFeature}
                    loadingProjectWorkers={newLoadingProjectWorkers}
                    loadingBudgetOptions={newLoadingBudgetOptions}
                    registerDropHandler={setOnDropHandler}
                    onEmployeeIdChange={setFormEmployeeId}
                    onEffectiveOnChange={onEffectiveOnChange}
                    canEditExpensesWithPendingStatus={false}
                    canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                    canCreateExpensesForOthers={canCreateExpensesForOthers}
                    hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                    rates={ratesOptions}
                    hasExpensablesF0Forms={props.hasExpensablesF0Forms}
                    onReuseTrip={showReuseButton ? handleReuseTrip : undefined}
                  />
                </Box>
                <CreateExpenseFooterActions
                  customizationFields={customizationFields}
                  onClose={handleClose}
                  groupId={groupId}
                  taxAgencyOrganization={null}
                  formType="mileage"
                />
              </>
            )}
          </FormProvider>
        </FilesPreviewerProvider>
      </Sidepanel>
    </BulkActionsProvider>
  )
}

export default CreateMileageForm
